import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ClipboardList, Plus, Trash2, Edit2, X, Target, Users, Play, Clock, Dumbbell, ChevronDown, ChevronUp, ExternalLink, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachTrainingPlans = () => {
  const { getAuthHeaders } = useAuth();
  const [plans, setPlans] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [duplicatingPlan, setDuplicatingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [formData, setFormData] = useState({
    athlete_id: '',
    name: '',
    sport: 'soccer',
    plan_type: 'weekly',
    objectives: [''],
    drill_ids: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [athletesRes, drillsRes] = await Promise.all([
        axios.get(`${API}/athletes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/drills`, { headers: getAuthHeaders() })
      ]);
      setAthletes(athletesRes.data);
      setDrills(drillsRes.data);
      
      // Fetch plans for each athlete with full drill data
      const allPlans = [];
      for (const athlete of athletesRes.data) {
        try {
          const planRes = await axios.get(`${API}/athletes/${athlete.id}/training-plan`, { headers: getAuthHeaders() });
          if (planRes.data && planRes.data.id) {
            allPlans.push({ ...planRes.data, athlete_name: athlete.name });
          }
        } catch (e) {
          // No plan for this athlete
        }
      }
      setPlans(allPlans);
    } catch (error) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        objectives: formData.objectives.filter(o => o.trim() !== ''),
        drills: [] // Clear legacy drills field
      };

      if (editingPlan) {
        await axios.put(`${API}/athletes/${formData.athlete_id}/training-plan`, payload, { headers: getAuthHeaders() });
        toast.success('Training plan updated');
      } else {
        await axios.post(`${API}/training-plans`, payload, { headers: getAuthHeaders() });
        toast.success('Training plan created');
      }
      closeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save training plan');
    }
    setSubmitting(false);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      athlete_id: plan.athlete_id,
      name: plan.name,
      sport: plan.sport || 'soccer',
      plan_type: plan.plan_type || 'weekly',
      objectives: plan.objectives?.length > 0 ? plan.objectives : [''],
      drill_ids: plan.drill_ids || [],
      notes: plan.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (planId, athleteId) => {
    if (!window.confirm('Delete this training plan?')) return;
    try {
      await axios.delete(`${API}/training-plans/${planId}`, { headers: getAuthHeaders() });
      toast.success('Training plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete training plan');
    }
  };

  const handleDuplicate = (plan) => {
    // Pre-fill form with plan data but clear athlete_id so user must select new athlete
    setDuplicatingPlan(plan);
    setFormData({
      athlete_id: '', // Force user to select different athlete
      name: `${plan.name} (Copy)`,
      sport: plan.sport || 'soccer',
      plan_type: plan.plan_type || 'weekly',
      objectives: plan.objectives?.length > 0 ? [...plan.objectives] : [''],
      drill_ids: plan.drill_ids || [],
      notes: plan.notes || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPlan(null);
    setDuplicatingPlan(null);
    setFormData({
      athlete_id: '',
      name: '',
      sport: 'soccer',
      plan_type: 'weekly',
      objectives: [''],
      drill_ids: [],
      notes: ''
    });
  };

  const addObjective = () => {
    setFormData({ ...formData, objectives: [...formData.objectives, ''] });
  };

  const updateObjective = (index, value) => {
    const newObjectives = [...formData.objectives];
    newObjectives[index] = value;
    setFormData({ ...formData, objectives: newObjectives });
  };

  const removeObjective = (index) => {
    const newObjectives = formData.objectives.filter((_, i) => i !== index);
    setFormData({ ...formData, objectives: newObjectives.length > 0 ? newObjectives : [''] });
  };

  const toggleDrill = (drillId) => {
    const exists = formData.drill_ids.includes(drillId);
    if (exists) {
      setFormData({ ...formData, drill_ids: formData.drill_ids.filter(id => id !== drillId) });
    } else {
      setFormData({ ...formData, drill_ids: [...formData.drill_ids, drillId] });
    }
  };

  const athletesWithoutPlan = athletes.filter(a => !plans.find(p => p.athlete_id === a.id));

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-400';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400';
      case 'advanced': return 'bg-red-500/20 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-training-plans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Training Plans</h1>
          <p className="text-muted-foreground">{plans.length} plans created</p>
        </div>
        <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Plan
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">
                {editingPlan ? 'Edit Training Plan' : duplicatingPlan ? 'Duplicate Training Plan' : 'New Training Plan'}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Athlete</label>
                  <select
                    value={formData.athlete_id}
                    onChange={(e) => setFormData({...formData, athlete_id: e.target.value})}
                    required
                    disabled={editingPlan}
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    <option value="" className="bg-card">Select Athlete</option>
                    {(editingPlan ? athletes : athletesWithoutPlan).map(a => (
                      <option key={a.id} value={a.id} className="bg-card">{a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Plan Name</label>
                  <Input
                    placeholder="e.g., Technical Focus Week 1"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {/* Sport field removed - Soccer only platform */}
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Plan Type</label>
                  <select
                    value={formData.plan_type}
                    onChange={(e) => setFormData({...formData, plan_type: e.target.value})}
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    <option value="weekly" className="bg-card">Weekly</option>
                    <option value="monthly" className="bg-card">Monthly</option>
                  </select>
                </div>
              </div>

              {/* Objectives */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Objectives</label>
                {formData.objectives.map((obj, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input
                      placeholder={`Objective ${idx + 1}`}
                      value={obj}
                      onChange={(e) => updateObjective(idx, e.target.value)}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeObjective(idx)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addObjective}>
                  <Plus size={14} className="mr-1" /> Add Objective
                </Button>
              </div>

              {/* Drill Selection from Library */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Select Drills from Library ({formData.drill_ids.length} selected)
                </label>
                {drills.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-sm">
                    No drills in your library. Go to Drill Library to add drills.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-sm p-2">
                    {drills.filter(d => d.sport === 'soccer' || !d.sport).map((drill) => {
                      const isSelected = formData.drill_ids.includes(drill.id);
                      return (
                        <div
                          key={drill.id}
                          onClick={() => toggleDrill(drill.id)}
                          className={`p-3 rounded-sm border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/10'
                              : 'border-border/40 hover:border-primary/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{drill.name}</span>
                                {drill.video_url && (
                                  <Play size={12} className="text-primary" />
                                )}
                              </div>
                              <MarkdownRenderer content={drill.description} truncate maxLines={1} className="text-xs mt-1" />
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <span className={`text-xs px-2 py-0.5 rounded ${getDifficultyColor(drill.difficulty)}`}>
                                {drill.difficulty}
                              </span>
                              <span className="text-xs text-muted-foreground">{drill.duration}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Coach Notes</label>
                <Textarea
                  placeholder="Additional notes for this plan..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                  {submitting ? 'Saving...' : editingPlan ? 'Update Plan' : duplicatingPlan ? 'Create Copy' : 'Create Plan'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No training plans yet</p>
          <Button onClick={() => setShowForm(true)}>Create Your First Plan</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-card border border-border/40 rounded-sm overflow-hidden">
              {/* Plan Header */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Users size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{plan.athlete_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{plan.plan_type}</span>
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">{plan.sport}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => handleDuplicate(plan)} 
                      className="p-1.5 hover:bg-muted rounded-sm text-blue-400"
                      title="Duplicate plan for another athlete"
                    >
                      <Copy size={14} />
                    </button>
                    <button onClick={() => handleEdit(plan)} className="p-1.5 hover:bg-muted rounded-sm">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(plan.id, plan.athlete_id)} className="p-1.5 hover:bg-muted rounded-sm text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {plan.objectives?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Objectives</p>
                    <ul className="space-y-1">
                      {plan.objectives.map((obj, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Target size={12} className="text-primary mt-1 flex-shrink-0" />
                          <span>{obj}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {plan.notes && (
                  <p className="text-sm text-muted-foreground italic mb-3">"{plan.notes}"</p>
                )}

                {/* Drills Summary */}
                {(plan.drills_full?.length > 0 || plan.drill_ids?.length > 0) && (
                  <button
                    onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Dumbbell size={14} />
                    <span>{plan.drills_full?.length || plan.drill_ids?.length} drill(s) assigned</span>
                    {expandedPlan === plan.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}

                <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/40">
                  Updated: {new Date(plan.updated_at).toLocaleDateString()}
                </p>
              </div>

              {/* Expanded Drills */}
              {expandedPlan === plan.id && plan.drills_full?.length > 0 && (
                <div className="border-t border-border/40 bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold uppercase mb-3">Assigned Drills</h4>
                  <div className="space-y-3">
                    {plan.drills_full.map((drill, idx) => (
                      <div key={drill.id || idx} className="bg-card border border-border/40 p-4 rounded-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{drill.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${getDifficultyColor(drill.difficulty)}`}>
                                {drill.difficulty}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-muted rounded">{drill.category}</span>
                            </div>
                            <div className="bg-muted/30 p-2 rounded-sm border border-border/40 mb-2">
                              <MarkdownRenderer content={drill.description} />
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {drill.duration}
                              </span>
                              {drill.equipment && (
                                <span>Equipment: {drill.equipment}</span>
                              )}
                              {drill.position_focus && (
                                <span>Position: {drill.position_focus}</span>
                              )}
                            </div>
                          </div>
                          {drill.video_url && (
                            <a
                              href={drill.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline text-sm ml-4"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Play size={14} />
                              Watch
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachTrainingPlans;
